import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import DOMPurify from 'dompurify';

import { ImageContext, ImageContextProvider } from '../context/imageContext.js'

import { MultiCarouselR } from './multiCarousel.js';
import { OpenSeaDragonViewer } from './osdViewer.js';

import '../../css/imageViewer.scoped.scss';

/*
FIXME: let OSD handle the sequence, just track the index# and tell MultiCarousel
*/

const ImageViewerItem = (props) => {

  const [ selDataIdx, setSelDataIdx ] = useState(props.selDataIdx);
  useEffect(() => {
    setSelDataIdx(props.selDataIdx);
  }, [props.selDataIdx]);

  const itemClickHandler = (event) => {
    event.preventDefault()

    props.itemClickAction(event, props.data, props.idx);
    setSelDataIdx(props.idx);
    props.setSelFunc(event);
  }

  const getFreeHeightThumb = (thumb) => {
    const i = thumb.indexOf('&h=');
    return thumb.substring(0, i);
  }

  return (
    <div className={`item ${props.itemClass}`}>
      <div className={`grid-viewer-container ${props.idx === selDataIdx ? 'selected' : ''}`}>
          <a className=""
            href="#"
            onClick={itemClickHandler}
            onFocus={props.focusfunc}
            aria-current={props.idx === selDataIdx ? 'true' : null}
            style={{userSelect: 'none'}}
          >
          <div className="grid-image-container-slider grid-viewslider">
            <figure>
              <img src={props.data.thumb}
                alt={props.data.alt ?? `Image ${props.idx + 1}`}
                style={{width: props.imgWidth, height: 'auto', maxHeight: 2 * props.imgWidth}}
              />
            </figure>
          </div>
          </a>
      </div>
      {/* <div className="image-viewer-thumb-num">
        {props.idx + 1}
      </div> */}
    </div>
  )
}

export function normalizeTileSource(image) {

  return {
          ...image,
          thumb: image.iiifThumb,
          label: image.label ?? '', // '<p>Placeholder Caption<br></p><p>With multiple lines</p>',
          tileSource: [`${image.iiifManifest}`]
          }
}

export const ImageViewer = (props) => {

  if (!props.images || props.images.length === 0) return null;

  const displaySlots = 5; // should be an odd number
  const initEmptySlots = Math.max(0, Math.floor((displaySlots - props.images.length) / 2));
  const initialDisplayIdx = Math.floor(displaySlots / 2) - initEmptySlots;

  const [ itemWidth, setItemWidth ] = useState(130);
  const [ imgWidth, setImgWidth ] = useState(96);

  const [ image, setImage ] = useState( props.images.length > 0 ? props.images[0] : '');
  const [ downloading, setDownloading ] = useState(false)

  const openImageInViewer = (e, data, idx) => {
    setImage(data);
    document.querySelector("#openSeaDragon .openseadragon-canvas").focus();
  }

  const downloadImage = async (e) => {
    if (!image) return

    // Examine image dimensions metadata and use 'full' if smaller than 2048
    const { height, width } = image
    const downloadSize = 2048
    const sizeParameter = height <= 2048 && width <= 2048 ? 'full' : `!${downloadSize},${downloadSize}`

    // Terminate the URL with a / to unsure URL() will path-add for us
    const terminatedId = image.id.endsWith('/') ? image.id : image.id + '/'
    const params = ['full',sizeParameter,'0','default.jpg'].join('/')

    const filenameStart = Boolean(props.filenameSlug) ? props.filenameSlug : 'access-okeeffe'  
    const filename = `${filenameStart}_${image.id.split('/').at(-1)}.jpg`

    // These will be revoked / killed on error
    let anchor, blobUrl
    try {
      const downloadUrl = new URL(params,terminatedId)

      setDownloading(true)
      const response = await fetch(downloadUrl)

      const blob = await response.blob()
      blobUrl = URL.createObjectURL(blob)

      anchor = document.createElement('a')
      anchor.href = blobUrl
      anchor.download = filename

      document.body.appendChild(anchor)

      anchor.click()
      setDownloading(false)
    } catch (err) {
      console.error("Encountered an error downloading the image:",err)

      if (anchor) document.body.removeChild(anchor)
      if (blobUrl) URL.revokeObjectURL(blobUrl)

      return
    } finally {
      document.body.removeChild(anchor)
      URL.revokeObjectURL(blobUrl)
    }
  }

  const showLegalModal = () => {
    window.postMessage({type: 'open-modal', id: 'legal-modal'}, window.origin)
  }

  const downloadOK = (event) => {
    const { data } = event

    if (data.type === 'user-ok' && data.id === 'legal-modal') {
      downloadImage()
    }
  }

  // Initialization -- add handler to receive download OK message
  useEffect(() => {
    window.addEventListener('message', downloadOK)
  },[])

  return (
    <div className="mx-0">
      <div className="px-0" >
        <div className='is-flex flex-direction-row'>
          <div className="tools-bar">
            {/* <button id="image-viewer__home" className={ `btn-icon-only icon-sm ${ props.darkIcons ? '' : 'is-dark-bg' }` }>home</button> */}
            <button id="image-viewer__zoom-in" className={ `btn-icon-only icon-sm ${ props.darkIcons ? '' : 'is-dark-bg' }` } title="Zoom in" data-tooltip="Zoom in">
              <span className="icon icon-zoom-in"></span>
              <span className="sr-only">Zoom in</span></button>
            <button id="image-viewer__zoom-out" className={ `btn-icon-only icon-sm ${ props.darkIcons ? '' : 'is-dark-bg' }` } title="Zoom out" data-tooltip="Zoom out">
              <span className="icon icon-zoom-out"></span>
              <span className="sr-only">Zoom out</span></button>
            <button id="image-viewer__revert" className={ `btn-icon-only icon-sm ${ props.darkIcons ? '' : 'is-dark-bg' }` } title="Rotate" data-tooltip="Rotate">
              <span className="icon icon-revert"></span>
              <span className="sr-only">Rotate</span></button>
            <button id="image-viewer__fullscreen" className={ `btn-icon-only icon-sm ${ props.darkIcons ? '' : 'is-dark-bg' }` } title="Fullscreen" data-tooltip="Fullscreen">
              <span className="icon icon-exp-window"></span>
              <span className="sr-only">Fullscreen</span></button>
            <button id="image-viewer__download"
                    className={`btn-icon-only icon-sm ${ props.darkIcons ? '' : 'is-dark-bg' } ${ image.download ? '' : 'is-hidden' }`}
                    title="Download" data-tooltip="Download"
                    onClick={showLegalModal}>
              <span className="icon icon-download"></span>
              <span className="sr-only">Download</span></button>
              <progress className={`image-viewer__progress progress is-small ${ downloading ? '' : 'is-hidden' }`} max="100">50%</progress>
          </div>
          <OpenSeaDragonViewer home
            homeTooltip={props.homeTooltip}
            zoomOutTooltip={props.zoomOutTooltip}
            zoomInTooltip={props.zoomInTooltip}
            fullPageTooltip={props.fullPageTooltip}
            image={image}
          />
        </div>
        <div className="entity-image-viewer-caption">
            { image.caption ?? '' }
        </div>
        <div className="entity-image-viewer-caption pt-2" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(image.label)}} ></div>
        <MultiCarouselR
          itemWidth={itemWidth}
          displaySlots={displaySlots}
          alignMode={'center'}
          alwaysShowButtons={false}
          maxWidth={614}
          variableWidth={false}
          imgWidth={imgWidth}
          buttonClass={'sliding-button'}
          selectedIdx={0}
        >
        {
          props.images.map((hit, idx) => {
              return <ImageViewerItem
                itemClickAction={openImageInViewer}
                itemClass={'viewer-slider'}
                data={hit}
                key={idx}
              />
          })
        }
        </MultiCarouselR>
      </div>
    </div>
  )
}

window.addEventListener('DOMContentLoaded', (event) => {

    if (document.getElementById('entityImageViewerRoot')) {
        ReactDOM.createRoot(document.getElementById('entityImageViewerRoot')).render(<ImageViewer {...entityImageViewerRoot.dataset} images={JSON.parse(entityImageViewerRoot.dataset.representation)?.map( item => normalizeTileSource(item) ) ?? []} />);
    }

})